import { Component } from "@theme/component";

/**
 * Hero/section video player with play/pause and optional mute toggle. The
 * article variant (`controls: 'article'` in snippets/video-player.liquid)
 * adds an elapsed / total time label, a progress bar, and a fullscreen toggle;
 * each is optional, so players without those refs are unaffected.
 *
 * @typedef {object} Refs
 * @property {HTMLVideoElement | HTMLVideoElement[] | undefined} videoElement
 * @property {HTMLButtonElement | HTMLButtonElement[]} playButton
 * @property {HTMLButtonElement | HTMLButtonElement[] | undefined} muteButton
 * @property {HTMLElement | undefined} timeLabel
 * @property {HTMLElement | undefined} progressBar
 * @property {HTMLButtonElement | undefined} fullscreenButton
 * @property {HTMLElement | undefined} fullscreenLabel
 * @extends {Component<Refs>}
 */
export class VideoPlayer extends Component {
  requiredRefs = ["playButton"];

  #onPlay = () => this.#updatePlayToggle(true);
  #onPause = () => this.#updatePlayToggle(false);
  #onVolumeChange = () => {
    const videoElement = this.#getVideoElement();
    if (videoElement) this.#updateMuteToggle(videoElement.muted);
  };
  #onTimeUpdate = () => this.#updateProgress();
  #onFullscreenChange = () => this.#updateFullscreenToggle();
  #reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  #deferredSources = null;
  #intersectionObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const videoElement = this.#getVideoElement();
    if (!videoElement) return;

    videoElement.addEventListener("play", this.#onPlay);
    videoElement.addEventListener("pause", this.#onPause);
    videoElement.addEventListener("volumechange", this.#onVolumeChange);
    for (const type of ["timeupdate", "loadedmetadata", "durationchange"]) {
      videoElement.addEventListener(type, this.#onTimeUpdate);
    }
    document.addEventListener("fullscreenchange", this.#onFullscreenChange);
    this.#setupFullscreenButton(videoElement);

    this.#reducedMotionQuery.addEventListener(
      "change",
      this.#handleReducedMotion,
    );

    this.#deferSources(videoElement);

    if (this.#reducedMotionQuery.matches) {
      // Don't even fetch the video bytes when the user has reduced-motion on.
      videoElement.pause();
    } else {
      this.#intersectionObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              this.#loadDeferredSources(videoElement);
              this.#intersectionObserver?.disconnect();
              this.#intersectionObserver = null;
              break;
            }
          }
        },
        { rootMargin: "200px" },
      );
      this.#intersectionObserver.observe(this);
    }

    this.#updatePlayToggle(!videoElement.paused);
    this.#updateMuteToggle(videoElement.muted);
    this.#updateProgress();
  }

  #deferSources(videoElement) {
    const sources = Array.from(videoElement.querySelectorAll("source"));
    if (sources.length === 0) return;
    this.#deferredSources = sources.map((source) => {
      const src = source.getAttribute("src");
      source.removeAttribute("src");
      return { source, src };
    });
  }

  #loadDeferredSources(videoElement) {
    if (!this.#deferredSources) return;
    let restored = false;
    for (const { source, src } of this.#deferredSources) {
      if (src) {
        source.setAttribute("src", src);
        restored = true;
      }
    }
    this.#deferredSources = null;
    if (restored) {
      videoElement.load();
      if (videoElement.autoplay) {
        void videoElement.play?.().catch(() => {});
      }
    }
  }

  disconnectedCallback() {
    const videoElement = this.#getVideoElement();

    videoElement?.removeEventListener("play", this.#onPlay);
    videoElement?.removeEventListener("pause", this.#onPause);
    videoElement?.removeEventListener("volumechange", this.#onVolumeChange);
    for (const type of ["timeupdate", "loadedmetadata", "durationchange"]) {
      videoElement?.removeEventListener(type, this.#onTimeUpdate);
    }
    document.removeEventListener("fullscreenchange", this.#onFullscreenChange);

    this.#reducedMotionQuery.removeEventListener(
      "change",
      this.#handleReducedMotion,
    );

    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;

    super.disconnectedCallback?.();
  }

  handleToggle(event) {
    event?.preventDefault?.();

    const videoElement = this.#getVideoElement();
    if (!videoElement) return;

    if (videoElement.paused) {
      this.#loadDeferredSources(videoElement);
      void videoElement.play?.().catch(() => {});
    } else {
      videoElement.pause();
    }
  }

  handleMuteToggle(event) {
    event?.preventDefault?.();

    const videoElement = this.#getVideoElement();
    if (!videoElement) return;

    videoElement.muted = !videoElement.muted;
  }

  /** Fullscreens the whole player so the custom controls stay visible. */
  handleFullscreen(event) {
    event?.preventDefault?.();

    if (document.fullscreenElement === this) {
      void document.exitFullscreen?.().catch(() => {});
      return;
    }
    if (this.requestFullscreen) {
      void this.requestFullscreen().catch(() => {});
      return;
    }
    // iOS Safari only fullscreens the video element itself.
    /** @type {any} */ (this.#getVideoElement())?.webkitEnterFullscreen?.();
  }

  #setupFullscreenButton(videoElement) {
    const button = this.refs.fullscreenButton;
    if (!button) return;
    const supported =
      document.fullscreenEnabled ||
      typeof (/** @type {any} */ (videoElement).webkitEnterFullscreen) ===
        "function";
    button.hidden = !supported;
    this.#updateFullscreenToggle();
  }

  #updateFullscreenToggle() {
    const button = this.refs.fullscreenButton;
    if (!button) return;
    const isFullscreen = document.fullscreenElement === this;
    const label = isFullscreen ? "Exit fullscreen" : "Fullscreen";
    if (this.refs.fullscreenLabel)
      this.refs.fullscreenLabel.textContent = label;
    button.setAttribute(
      "aria-label",
      isFullscreen ? "Exit fullscreen" : "Fullscreen video",
    );
  }

  #updateProgress() {
    const { timeLabel, progressBar } = this.refs;
    if (!timeLabel && !progressBar) return;

    const videoElement = this.#getVideoElement();
    const duration = videoElement?.duration;
    if (!videoElement || !Number.isFinite(duration) || duration <= 0) return;

    const current = videoElement.currentTime;
    if (progressBar) {
      progressBar.style.transform = `scaleX(${Math.min(current / duration, 1)})`;
    }
    if (timeLabel) {
      timeLabel.textContent = `${formatTime(current)} / ${formatTime(duration)}`;
    }
  }

  #handleReducedMotion = (event) => {
    const videoElement = this.#getVideoElement();
    if (!videoElement) return;

    if (event.matches) {
      videoElement.pause();
    }
  };

  #updatePlayToggle(isPlaying) {
    const button = this.#getRef("playButton");
    if (!button) return;

    button.dataset.state = isPlaying ? "playing" : "paused";
    button.setAttribute("aria-pressed", isPlaying ? "true" : "false");
    button.setAttribute("aria-label", isPlaying ? "Pause video" : "Play video");
  }

  #updateMuteToggle(isMuted) {
    const button = this.#getRef("muteButton");
    if (!button) return;

    button.dataset.state = isMuted ? "muted" : "unmuted";
    button.setAttribute("aria-pressed", isMuted ? "true" : "false");
    button.setAttribute("aria-label", isMuted ? "Unmute video" : "Mute video");
  }

  #getRef(name) {
    const ref = this.refs[name];
    if (!ref) return null;
    if (Array.isArray(ref)) return ref[0] ?? null;
    return ref;
  }

  #getVideoElement() {
    const { videoElement } = this.refs;

    if (videoElement) {
      if (Array.isArray(videoElement)) return videoElement[0] ?? null;
      return videoElement;
    }

    const fallback = this.querySelector("video");
    return fallback instanceof HTMLVideoElement ? fallback : null;
  }
}

/**
 * Formats seconds as m:ss (e.g. 0:32, 2:41).
 *
 * @param {number} seconds
 */
function formatTime(seconds) {
  const total = Math.max(0, Math.floor(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

if (!customElements.get("video-player")) {
  customElements.define("video-player", VideoPlayer);
}
